import { Body02, ChevronLeftIcon } from 'Components/designSystem';
import React, { memo, useMemo } from 'react';
import { Skeleton } from 'shared/components';
import { ellipsis } from 'shared/styles';
import { MAX_CONTAINER_WIDTH } from 'shared/styles/ContentContainer';
import styled from 'styled-components';

interface PageTopPanelProps {
    className?: string;
    text?: string;
    onBackClick?: () => void;
    loading?: boolean;
    disabled?: boolean;
    actions?: React.ReactNode[];
}

function PageTopPanel(props: PageTopPanelProps) {
    const { text, className, onBackClick, loading, disabled, actions } = props;

    const textElement = useMemo(() => {
        if (loading) {
            return <Skeleton width={ 120 } />;
        }

        if (!text) {
            return null;
        }

        return (
            <Title medium
                as="span"
            >
                { text }
            </Title>
        );
    }, [ text, loading ]);

    return (
        <Root className={ className }>
            <BackButton onClick={ onBackClick }
                disabled={ disabled || loading }
            >
                <IconContainer>
                    <BackIcon />
                </IconContainer>
                { textElement }
            </BackButton>
            { actions && (
                <ActionsContainer>{ actions }</ActionsContainer>
            ) }
        </Root>
    );
}

export default memo(PageTopPanel);

const Root = styled.div`
    width: ${ MAX_CONTAINER_WIDTH }px;
    max-width: ${ MAX_CONTAINER_WIDTH }px;
    padding: ${ ({ theme }) => `${ theme.size.size_20 } ${ theme.size.size_30 }` };
    background-color: ${ ({ theme }) => theme.color.white };
    margin: 0 auto;
    display: flex;
    justify-content: space-between;

    @media ${ ({ theme }) => theme.breakpoints.mediaQueries.isMobile } {
        max-width: 100%;
        width: 100%;
        padding: ${ ({ theme }) => `${ theme.size.size_20 } ${ theme.size.size_20 }` };
    }
`;

const ActionsContainer = styled.div`
    display: flex;
    gap: ${ ({ theme }) => theme.size.size_20 };
`;

const BackIcon = styled(ChevronLeftIcon)`
    fill: ${ ({ theme }) => theme.color.grey.grey_80 };
`;

const Title = styled(Body02)`
    ${ ellipsis }
`;

const IconContainer = styled.span`
    width: ${ ({ theme }) => theme.size.size_25 };
    height: ${ ({ theme }) => theme.size.size_25 };
    padding: ${ ({ theme }) => theme.size.size_2 }; 0;
    position: relative;
    overflow: visible;

    &:before {
        content: "";
        display: none;
        position: absolute;
        width: ${ ({ theme }) => theme.size.size_30 };
        height: ${ ({ theme }) => theme.size.size_30 };
        top: -${ ({ theme }) => theme.size.size_2 };
        left: -${ ({ theme }) => theme.size.size_2 };
        margin: auto;
        opacity: 0.1;
        background-color: ${ ({ theme }) => theme.color.grey.grey_80 };
        border-radius: 50%;
    }
`;

const BackButton = styled.button.attrs({
    type: 'button'
})`
    padding: 0;
    background: none;
    border: none;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${ ({ theme }) => theme.size.size_5 };
    outline: none;

    &:not(:disabled) {
        cursor: pointer;

        &:hover, &:active {
            ${ IconContainer }:before {
                display: block;
            }
        }

        &:focus-visible {
            ${ IconContainer }:before {
                display: block;
                box-shadow: ${ ({ theme }) => `0px 0px 0px 2px ${ theme.semanticColor.focus }` };
                background: transparent;
                opacity: 1;
            }
        }
    }
`;
